import axios from 'axios';
import { currentOperationKey, rotateOperationKey } from './idempotency';

const getBackendUrl = () => {
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;

  // Mapowanie domen na adresy backendów
  const domainMapping = {
    'localhost': 'http://localhost:8100',
    '127.0.0.1': 'http://localhost:8100',
    '192.168.1.100': 'http://192.168.1.100:8300',
    'farm.stec.dev': 'https://farm.stec.dev',
  };

  // Sprawdź czy mamy bezpośrednie mapowanie dla tej domeny
  if (domainMapping[currentHost]) {
    return domainMapping[currentHost];
  }

  // Dla IP lub nieznanych domen, spróbuj na tym samym hoście z portem 8100
  return `${currentProtocol}//${currentHost}:8100`;
};

const API_BASE_URL = getBackendUrl();
console.log('API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const isMutating = (config) => (config?.method || 'get').toLowerCase() !== 'get';

// Request interceptor - attach token and the key of the operation in progress
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const operationKey = currentOperationKey();
    if (operationKey && isMutating(config)) {
      config.headers['Idempotency-Key'] = operationKey;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - rotate the operation key, handle 401
api.interceptors.response.use(
  (response) => {
    // The operation succeeded, so anything submitted next is a new one. Failures deliberately keep
    // the key, which is what makes retrying a timed-out request safe.
    if (isMutating(response.config)) {
      rotateOperationKey();
    }
    return response;
  },
  (error) => {
    // A 401 from a credential check means "those credentials were wrong", not "your session ended",
    // so it must not wipe the token and bounce the page.
    const url = error.config?.url || '';
    const isCredentialCheck =
      url.includes('/auth/login') || url.includes('/auth/passkeys/authentication');
    if (error.response && error.response.status === 401 && !isCredentialCheck) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Passkeys. The two authentication calls are the only ones reachable without a token.
export const passkeyApi = {
  registrationOptions: () => api.post('/auth/passkeys/registration/options'),
  register: (payload) => api.post('/auth/passkeys/registration', payload),
  authenticationOptions: () => api.post('/auth/passkeys/authentication/options'),
  authenticate: (payload) => api.post('/auth/passkeys/authentication', payload),
  list: () => api.get('/auth/passkeys'),
  delete: (credentialId) => api.delete(`/auth/passkeys/${encodeURIComponent(credentialId)}`),
};

// Factory for the standard REST CRUD shape shared by most resources.
// Compose with spread to add resource-specific endpoints:
//   export const fooApi = { ...createCrudApi('foos'), getBySeason: ... };
const createCrudApi = (resource) => ({
  getAll: () => api.get(`/${resource}`),
  getById: (id) => api.get(`/${resource}/${id}`),
  create: (data) => api.post(`/${resource}`, data),
  update: (id, data) => api.put(`/${resource}/${id}`, data),
  delete: (id) => api.delete(`/${resource}/${id}`),
});

// Warehouse API
export const warehouseApi = createCrudApi('warehouses');

// Warehouse Season API - New season-based warehouse management
export const warehouseSeasonApi = {
  ...createCrudApi('warehouse-seasons'),
  getByWarehouseId: (warehouseId) => api.get(`/warehouse-seasons/warehouse/${warehouseId}`),
  getByYear: (year) => api.get(`/warehouse-seasons?season=${year}`),
  getCurrentSeason: () => api.get('/warehouse-seasons/current-season'),
  getAvailableYears: () => api.get('/warehouse-seasons/available-years'),
  getCropStocks: (warehouseSeasonId) => api.get(`/warehouse-seasons/${warehouseSeasonId}/crop-stocks`),
  initializeFromTemplates: (seasonYear) => api.post(`/warehouse-seasons/initialize?season=${seasonYear}`),
  rebuildAllCropStocks: () => api.post('/warehouse-seasons/rebuild-crop-stocks'),
};

// Field API
export const fieldApi = createCrudApi('fields');

// Field Season API
export const fieldSeasonApi = {
  ...createCrudApi('field-seasons'),
  getByFieldId: (fieldId) => api.get(`/field-seasons/field/${fieldId}`),
  getByYear: (year) => api.get(`/field-seasons?season=${year}`),
  getCurrentSeason: () => api.get('/field-seasons/current-season'),
  getCropTypes: () => api.get('/field-seasons/crop-types'),
  getAvailableYears: () => api.get('/field-seasons/available-years'),
};

// Trailer API
export const trailerApi = createCrudApi('trailers');

// AgShare (AgOpenGPS) API — administration only. The tractor terminals talk to
// /api/agshare/fields with their own ApiKey scheme and never touch these endpoints.
export const agShareApi = {
  getFields: () => api.get('/agshare/admin/fields'),
  getField: (fieldId) => api.get(`/agshare/admin/fields/${fieldId}`),
  setVisibility: (fieldId, isPublic) =>
    api.put(`/agshare/admin/fields/${fieldId}/visibility/${isPublic}`),
  saveBoundary: (fieldId, boundary) =>
    api.put(`/agshare/admin/fields/${fieldId}/boundary`, boundary),
  linkFarmField: (fieldId, farmFieldId) =>
    api.put(`/agshare/admin/fields/${fieldId}/link/${farmFieldId}`),
  unlinkFarmField: (fieldId) => api.delete(`/agshare/admin/fields/${fieldId}/link`),
  deleteField: (fieldId) => api.delete(`/agshare/admin/fields/${fieldId}`),
  getTerminals: () => api.get('/agshare/admin/terminals'),
  createTerminal: (data) => api.post('/agshare/admin/terminals', data),
  updateTerminal: (terminalId, data) => api.put(`/agshare/admin/terminals/${terminalId}`, data),
  deleteTerminal: (terminalId) => api.delete(`/agshare/admin/terminals/${terminalId}`),
};

// Delivery API
export const deliveryApi = {
  ...createCrudApi('deliveries'),
  getByWarehouseSeason: (warehouseSeasonId) => api.get(`/deliveries/warehouse-season/${warehouseSeasonId}`),
  getByFieldSeason: (fieldId) => api.get(`/deliveries/field/${fieldId}`),
  getBySeason: (seasonYear) => api.get(`/deliveries?season=${seasonYear}`),
  getStatistics: (fieldId, seasonYear) => {
    const params = new URLSearchParams();
    if (fieldId) params.append('fieldId', fieldId);
    if (seasonYear) params.append('seasonYear', seasonYear);
    return api.get(`/deliveries/statistics?${params.toString()}`);
  },
  createBulk: (bulkDelivery) => api.post('/deliveries/bulk', bulkDelivery),
};

// Warehouse Transfer API
export const warehouseTransferApi = {
  ...createCrudApi('warehouse-transfers'),
  getByWarehouse: (warehouseId) => api.get(`/warehouse-transfers/warehouse/${warehouseId}`),
  getByWarehouseSeason: (warehouseSeasonId) => api.get(`/warehouse-transfers/warehouse-season/${warehouseSeasonId}`),
  getBySeason: (seasonYear) => api.get(`/warehouse-transfers?season=${seasonYear}`),
  getCurrentStock: (warehouseId) => api.get(`/warehouse-transfers/warehouse/${warehouseId}/stock`),
  getCurrentStockBySeason: (warehouseSeasonId) => api.get(`/warehouse-transfers/warehouse-season/${warehouseSeasonId}/stock`),
  getCropComposition: (warehouseSeasonId) => api.get(`/warehouse-transfers/warehouse/${warehouseSeasonId}/crop-composition`),
};

// Contractor API
export const contractorApi = {
  ...createCrudApi('contractors'),
  getStatsBySeason: (seasonYear) => api.get(`/contractors/statistics?season=${seasonYear}`),
};

// Sale API
export const saleApi = {
  ...createCrudApi('sales'),
  getBySeason: (seasonYear) => api.get(`/sales?season=${seasonYear}`),
  getByWarehouse: (warehouseId) => api.get(`/sales/warehouse/${warehouseId}`),
  createBulk: (bulkSale) => api.post('/sales/bulk', bulkSale),
};

// System Configuration API
export const systemConfigApi = {
  ...createCrudApi('system-config'),
  getByKey: (key) => api.get(`/system-config/${key}`),
};

// Field Treatment API
export const fieldTreatmentApi = {
  ...createCrudApi('field-treatments'),
  getBySeason: (seasonYear) => api.get(`/field-treatments?season=${seasonYear}`),
  getBySeasonAndField: (seasonYear, fieldId) => api.get(`/field-treatments/field/${fieldId}?season=${seasonYear}`),
  getByFieldSeason: (fieldSeasonId) => api.get(`/field-treatments?fieldSeasonId=${fieldSeasonId}`),
  getTotalCost: (fieldSeasonId) => api.get(`/field-treatments/total-cost?fieldSeasonId=${fieldSeasonId}`),
  getTreatmentTypes: () => api.get('/field-treatments/treatment-types'),
  getAvailableTreatmentTypes: (season) => api.get(`/field-treatments/available-types?season=${season}`),
  getAvailableFertilizers: (seasonYear) => api.get(`/field-treatments/available-fertilizers?season=${seasonYear}`),
  getAvailableSprayingProducts: (seasonYear) => api.get(`/field-treatments/available-spraying-products?season=${seasonYear}`),
  createBulk: (bulkTreatment) => api.post('/field-treatments/bulk', bulkTreatment),
  updateBulk: (bulkTreatmentEdit) => api.put('/field-treatments/bulk', bulkTreatmentEdit),
  deleteBulk: (treatmentIds) => api.delete(`/field-treatments/bulk?ids=${treatmentIds.join(',')}`),
};

// Season Price API (covers both crop prices and treatment prices)
export const seasonPriceApi = {
  getAll: () => api.get('/finance/season-prices'),
  getBySeason: (seasonYear) => api.get(`/finance/season-prices?season=${seasonYear}`),
  save: (seasonYear, fields) => api.post('/finance/season-prices', { seasonYear, fields }),
  delete: (id) => api.delete(`/finance/season-prices/${id}`),
  initializeDefaults: (seasonYear) => api.post('/finance/season-prices/initialize-defaults', { seasonYear }),
  getCropTypes: () => api.get('/finance/season-prices/crop-types'),
};

// Report Dashboard API
export const reportApi = {
  getAll: () => api.get('/report'),
  getByYear: (year) => api.get(`/report?season=${year}`),
  getByFieldSeason: (fieldSeasonId) => api.get(`/report?fieldSeasonId=${fieldSeasonId}`),
};

// Warehouse Dashboard API
export const warehouseDashboardApi = {
  getAll: () => api.get('/warehouse-dashboard'),
  getBySeason: (seasonYear) => api.get(`/warehouse-dashboard?season=${seasonYear}`),
  getById: (warehouseId) => api.get(`/warehouse-dashboard/${warehouseId}`),
};

// Field Operational Cost API
export const fieldOperationalCostApi = {
  getAll: () => api.get('/finance/fields/operational-costs'),
  getById: (id) => api.get(`/finance/fields/operational-costs/${id}`),
  getByFieldSeasonId: (fieldSeasonId) => api.get(`/finance/fields/operational-costs?fieldSeasonId=${fieldSeasonId}`),
  getBySeasonYear: (seasonYear) => api.get(`/finance/fields/operational-costs?season=${seasonYear}`),
  getByFieldIdAndSeasonYear: (fieldId, seasonYear) => api.get(`/finance/fields/operational-costs/field/${fieldId}?season=${seasonYear}`),
  getCostTypes: () => api.get('/finance/fields/operational-costs/cost-types'),
  create: (cost) => api.post('/finance/fields/operational-costs', cost),
  update: (id, cost) => api.put(`/finance/fields/operational-costs/${id}`, cost),
  delete: (id) => api.delete(`/finance/fields/operational-costs/${id}`),
  createBulk: (bulk) => api.post('/finance/fields/operational-costs/bulk', bulk),
};

// Field Subsidy API
export const fieldSubsidyApi = {
  getAll: () => api.get('/finance/fields/subsidies'),
  getById: (id) => api.get(`/finance/fields/subsidies/${id}`),
  getByFieldSeasonId: (fieldSeasonId) => api.get(`/finance/fields/subsidies?fieldSeasonId=${fieldSeasonId}`),
  getBySeasonYear: (seasonYear) => api.get(`/finance/fields/subsidies?season=${seasonYear}`),
  getByFieldIdAndSeasonYear: (fieldId, seasonYear) => api.get(`/finance/fields/subsidies/field/${fieldId}?season=${seasonYear}`),
  getSubsidyTypes: () => api.get('/finance/fields/subsidies/subsidy-types'),
  create: (subsidy) => api.post('/finance/fields/subsidies', subsidy),
  update: (id, subsidy) => api.put(`/finance/fields/subsidies/${id}`, subsidy),
  delete: (id) => api.delete(`/finance/fields/subsidies/${id}`),
  createBulk: (bulk) => api.post('/finance/fields/subsidies/bulk', bulk),
};

// Fertilizer API
export const fertilizerApi = {
  getAll: () => api.get('/fertilization/fertilizers'),
  getById: (id) => api.get(`/fertilization/fertilizers/${id}`),
  getByName: (name) => api.get(`/fertilization/fertilizers/name/${name}`),
  create: (fertilizer) => api.post('/fertilization/fertilizers', fertilizer),
  update: (id, fertilizer) => api.put(`/fertilization/fertilizers/${id}`, fertilizer),
  delete: (id) => api.delete(`/fertilization/fertilizers/${id}`),
};

// Fertilizer Stock API
export const fertilizerStockApi = {
  getAll: () => api.get('/fertilization/stocks'),
  getBySeason: (seasonYear) => api.get(`/fertilization/stocks?season=${seasonYear}`),
  getById: (id) => api.get(`/fertilization/stocks/${id}`),
  getByFertilizerAndSeason: (fertilizerId, seasonYear) => api.get(`/fertilization/stocks/fertilizer/${fertilizerId}?season=${seasonYear}`),
  getAvailableYears: () => api.get('/fertilization/stocks/available-years'),
  create: (fertilizerStock) => api.post('/fertilization/stocks', fertilizerStock),
  update: (id, fertilizerStock) => api.put(`/fertilization/stocks/${id}`, fertilizerStock),
  delete: (id) => api.delete(`/fertilization/stocks/${id}`),
};

// Fertilizer Stock Balance API
export const fertilizerBalanceApi = {
  getBySeason: (seasonYear) => api.get(`/fertilizer-balance?season=${seasonYear}`),
};

// Crop Nutrient Norm API
export const cropNutrientNormApi = {
  getAll: () => api.get('/nutrition/norms'),
  getById: (id) => api.get(`/nutrition/norms/${id}`),
  create: (norm) => api.post('/nutrition/norms', norm),
  update: (id, norm) => api.put(`/nutrition/norms/${id}`, norm),
};

// Field Nutrient Target API
export const fieldNutrientTargetApi = {
  getBySeason: (seasonYear) => api.get(`/nutrition/targets?season=${seasonYear}`),
  getByFieldSeason: (fieldSeasonId) => api.get(`/nutrition/targets/field-season/${fieldSeasonId}`),
  create: (target) => api.post('/nutrition/targets', target),
  createBulk: (bulkTarget) => api.post('/nutrition/targets/bulk', bulkTarget),
  update: (id, target) => api.put(`/nutrition/targets/${id}`, target),
  delete: (id) => api.delete(`/nutrition/targets/${id}`),
};

// Organic Amendment Norm API
export const organicAmendmentNormApi = {
  getAll: () => api.get('/nutrition/organic-norms'),
  getById: (id) => api.get(`/nutrition/organic-norms/${id}`),
  create: (norm) => api.post('/nutrition/organic-norms', norm),
  update: (id, norm) => api.put(`/nutrition/organic-norms/${id}`, norm),
};

// Nutrient Balance API
export const nutrientBalanceApi = {
  getBySeason: (seasonYear) => api.get(`/nutrition/balance/season/${seasonYear}`),
};

// Fertilizer Suggestion API
export const fertilizerSuggestionApi = {
  getBySeason: (seasonYear) => api.get(`/nutrition/suggestions/season/${seasonYear}`),
};

// Soil Analysis API
export const soilAnalysisApi = {
  getLatestPerField: () => api.get('/soil/analyses'),
  getByFieldId: (fieldId) => api.get(`/soil/analyses?fieldId=${fieldId}`),
  getApplicableForSeason: (seasonYear) => api.get(`/soil/analyses/season/${seasonYear}`),
  getClassThresholds: () => api.get('/soil/analyses/thresholds'),
  getById: (id) => api.get(`/soil/analyses/${id}`),
  create: (soilAnalysis) => api.post('/soil/analyses', soilAnalysis),
  createBulk: (bulkAnalyses) => api.post('/soil/analyses/bulk', bulkAnalyses),
  update: (id, soilAnalysis) => api.put(`/soil/analyses/${id}`, soilAnalysis),
  delete: (id) => api.delete(`/soil/analyses/${id}`),
};

// Harvest Quality API — lab results for the harvested crop of a field season
export const harvestQualityApi = {
  getBySeason: (seasonYear) => api.get(`/quality/analyses?season=${seasonYear}`),
  getByFieldSeasonId: (fieldSeasonId) => api.get(`/quality/analyses/field-season/${fieldSeasonId}`),
  getByFieldId: (fieldId) => api.get(`/quality/analyses/field/${fieldId}`),
  getParameters: () => api.get('/quality/parameters'),
  getById: (id) => api.get(`/quality/analyses/${id}`),
  create: (analysis) => api.post('/quality/analyses', analysis),
  createBulk: (bulkAnalyses) => api.post('/quality/analyses/bulk', bulkAnalyses),
  update: (id, analysis) => api.put(`/quality/analyses/${id}`, analysis),
  delete: (id) => api.delete(`/quality/analyses/${id}`),
};

// Spraying Product API
export const sprayingProductApi = {
  getAll: () => api.get('/spraying/products'),
  getBySeason: (seasonYear) => api.get(`/spraying/products?season=${seasonYear}`),
  search: ({ query = '', page = 0, size = 25 } = {}) =>
    api.get('/spraying/products/search', { params: { query, page, size } }),
  getById: (id) => api.get(`/spraying/products/${id}`),
  getAvailableYears: () => api.get('/spraying/products/available-years'),
  create: (sprayingProduct) => api.post('/spraying/products', sprayingProduct),
  update: (id, sprayingProduct) => api.put(`/spraying/products/${id}`, sprayingProduct),
  delete: (id) => api.delete(`/spraying/products/${id}`),
  savePrice: (id, price) => api.put(`/spraying/products/${id}/prices`, price),
  deletePrice: (id, seasonYear) => api.delete(`/spraying/products/${id}/prices/${seasonYear}`),
  importPrices: (importRequest) => api.post('/spraying/products/prices/import', importRequest),
};

// Fertilization Plan API
// Compliance API — statutory registers
export const sprayingRegisterApi = {
  getBySeason: (seasonYear) => api.get(`/compliance/spraying-register?season=${seasonYear}`),
};

export const fertilizationPlanApi = {
  getAll: () => api.get('/fertilization/plans'),
  getBySeason: (seasonYear) => api.get(`/fertilization/plans?season=${seasonYear}`),
  getByFieldSeason: (fieldSeasonId) => api.get(`/fertilization/plans?fieldSeasonId=${fieldSeasonId}`),
  getById: (id) => api.get(`/fertilization/plans/${id}`),
  getAvailableYears: () => api.get('/fertilization/plans/available-years'),
  create: (fertilizationPlan) => api.post('/fertilization/plans', fertilizationPlan),
  createBulk: (bulkPlan) => api.post('/fertilization/plans/bulk', bulkPlan),
  savePlanLines: (planLines) => api.post('/fertilization/plans/field-season-lines', planLines),
  update: (id, fertilizationPlan) => api.put(`/fertilization/plans/${id}`, fertilizationPlan),
  delete: (id) => api.delete(`/fertilization/plans/${id}`),
  deleteAllForFieldSeason: (fieldSeasonId) => api.delete(`/fertilization/plans/all?fieldSeasonId=${fieldSeasonId}`),
};

// Machine API
export const machineApi = {
  getAll: () => api.get('/machines'),
  getById: (id) => api.get(`/machines/${id}`),
  getTypes: () => api.get('/machines/types'),
  create: (machine) => api.post('/machines', machine),
  update: (id, machine) => api.put(`/machines/${id}`, machine),
  delete: (id) => api.delete(`/machines/${id}`),
};

// Service Interval API
export const serviceIntervalApi = {
  getByMachine: (machineId) => api.get(`/service-intervals/machine/${machineId}`),
  create: (fields) => api.post('/service-intervals', fields),
  update: (id, fields) => api.put(`/service-intervals/${id}`, fields),
  delete: (id) => api.delete(`/service-intervals/${id}`),
};

// Part API
export const partApi = {
  getAll: () => api.get('/parts'),
  getByMachine: (machineId) => api.get(`/parts/machine/${machineId}`),
  getCategories: () => api.get('/parts/categories'),
  create: (part) => api.post('/parts', part),
  update: (id, part) => api.put(`/parts/${id}`, part),
  delete: (id) => api.delete(`/parts/${id}`),
};

// Service Entry API
export const serviceEntryApi = {
  getByMachine: (machineId) => api.get(`/service-entries/machine/${machineId}`),
  create: (entry) => api.post('/service-entries', entry),
  update: (id, entry) => api.put(`/service-entries/${id}`, entry),
  delete: (id) => api.delete(`/service-entries/${id}`),
};

export default api;
